/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import {
  Box,
  MenuItem,
  Button,
  Paper,
  InputLabel,
  FormHelperText,
  Collapse,
  Checkbox,
  FormControlLabel,
  Typography,
  IconButton,
  InputAdornment,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

import CustomDatePicker from './custom-date-picker';

import { useSearch } from '@/hooks/useSearch';
import { useAutocomplete } from '@/hooks/use-autocomplete';
import { useIsMobile } from '@/hooks/use-is-mobile';
import { GlobalSearchProps, SearchFieldConfig, SearchOption } from '@/types/search';
import {
  GlobalTextField,
  GlobalSelect,
  GlobalFormControl,
  GlobalAutocomplete,
} from '@/components/styles';
import { getOptionKey } from '@/utils/helpers';

const SearchContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.spacing(1),
  boxShadow: '0 2px 8px rgba(0,0,0,0.5)',
  width: '100%',
  maxWidth: '100%',
}));

const SearchHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(1),
}));

const ExpandButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  color: theme.palette.text.primary,
  padding: theme.spacing(0.5, 1),
  minWidth: 'auto',
  fontSize: '0.875rem',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  margin: '0 auto',
  '&:hover': {
    backgroundColor: 'transparent',
  },
}));

const SearchForm = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: '100%',
  '& .MuiFormControl-root': {
    marginBottom: theme.spacing(1),
    width: '100%',
    maxWidth: '100%',
  },
}));

const ActionButtons = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  // marginTop: theme.spacing(2),
  justifyContent: 'flex-start',
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    marginTop: theme.spacing(2),
  },
}));

const SearchButton = styled(Button)(({ theme }) => ({
  minWidth: 120,
  textTransform: 'none',
  height: '32px',
  borderRadius: theme.spacing(3),
  border: `1px solid ${theme.palette.primary.main}`,
  [theme.breakpoints.down('sm')]: {
    height: '40px',
    flex: 1,
  },
  '&:hover': {
    border: `1px solid ${theme.palette.primary.dark}`,
  },
}));

const ClearButton = styled(Button)(({ theme }) => ({
  size: 'small',
  minWidth: 120,
  textTransform: 'none',
  height: '32px',
  borderRadius: theme.spacing(3),
  color: theme.palette.text.secondary,
  border: `1px solid ${theme.palette.grey[300]}`,
  backgroundColor: 'transparent',
  [theme.breakpoints.down('sm')]: {
    height: '40px',
    flex: 1,
  },
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    border: `1px solid ${theme.palette.grey[400]}`,
  },
  '&:disabled': {
    border: `1px solid ${theme.palette.grey[200]}`,
    color: theme.palette.grey[400],
  },
}));

// Helper function to determine grid template columns based on field count
const getGridTemplateColumns = (fieldCount: number): string => {
  switch (fieldCount) {
    case 1:
      return '1fr';
    case 2:
      return 'repeat(2, 1fr)';
    case 3:
      return 'repeat(3, 1fr)';
    case 4:
      return 'repeat(4, 1fr)';
    default:
      return 'repeat(7, 1fr)';
  }
};

// Extracted renderInput component to avoid inline definition
const AutocompleteRenderInput = ({
  params,
  field,
  error,
  touched,
}: {
  params: any;
  field: SearchFieldConfig;
  error: string;
  touched: boolean;
}) => (
  <GlobalTextField
    {...params}
    label={field.label}
    placeholder={field.placeholder}
    error={touched && !!error}
    helperText={touched && error ? error : ''}
    size='small'
    slotProps={{
      input: {
        ...params.InputProps,
      },
    }}
  />
);

// *** NEW HOOK: Extracted duplicate logic ***
// This hook manages the clear function registration for autocomplete fields
const useAutocompleteClear = (
  clearAutocomplete: () => void,
  onChange: (key: string, value: any) => void,
  fieldKey: string,
  onRegisterClear?: (clearFn: () => void) => void
) => {
  // Enhanced clear function that also clears form state
  const clear = useCallback(() => {
    clearAutocomplete();
    onChange(fieldKey, '');
  }, [clearAutocomplete, onChange, fieldKey]); // Register clear function with parent

  React.useEffect(() => {
    if (onRegisterClear) {
      onRegisterClear(clear);
    }
  }, [clear, onRegisterClear]);
};

// Static Autocomplete Component (for pre-loaded options)
const StaticAutocompleteField: React.FC<{
  field: SearchFieldConfig;
  value: any;
  error: string;
  touched: boolean;
  onChange: (key: string, value: any) => void;
  onRegisterClear?: (clearFn: () => void) => void;
}> = ({ field, value, error, touched, onChange, onRegisterClear }) => {
  // Use useAutocomplete for filtering
  const {
    options,
    loading,
    inputValue,
    selectedValue,
    handleInputChange,
    handleValueChange,
    handleFocus,
    clear: clearAutocomplete,
  } = useAutocomplete(async () => field.options || [], value); // *** CHANGED: Use the new hook ***
  // Use the extracted hook to handle clear logic

  useAutocompleteClear(clearAutocomplete, onChange, field.key, onRegisterClear); // *** REMOVED DUPLICATE BLOCK ***
  // const clear = useCallback(...)
  // React.useEffect(...)

  return (
    <GlobalAutocomplete
      key={`${field.key}-${selectedValue ? 'has-value' : 'no-value'}`}
      fullWidth
      options={options}
      value={selectedValue}
      inputValue={inputValue}
      onChange={(_, newValue: any) => {
        if (typeof newValue === 'string') {
          onChange(field.key, newValue);
        } else {
          handleValueChange(newValue);
          onChange(field.key, newValue?.value || '');
        }
      }}
      onInputChange={(_, newInputValue, reason) => {
        handleInputChange(newInputValue);
        if (reason === 'input' && field.freeSolo) {
          onChange(field.key, newInputValue);
        }
      }}
      onFocus={handleFocus}
      getOptionLabel={(option: any) => {
        if (typeof option === 'string') return option;
        return option.label || '';
      }}
      isOptionEqualToValue={(option: any, value: any) => {
        if (typeof option === 'string' || typeof value === 'string') {
          return option === value;
        }
        return option.value === value?.value;
      }}
      loading={loading}
      freeSolo={field.freeSolo}
      selectOnFocus
      clearOnBlur={false}
      handleHomeEndKeys
      noOptionsText={inputValue ? 'データが見つかりません' : '入力してください'}
      slotProps={{
        listbox: {
          style: {
            maxWidth: '300px',
          },
        },
      }}
      renderInput={(params) => (
        <AutocompleteRenderInput params={params} field={field} error={error} touched={touched} />
      )}
    />
  );
};

// Dynamic Autocomplete Component (with fetchOptions)
const DynamicAutocompleteField: React.FC<{
  field: SearchFieldConfig;
  value: any;
  error: string;
  touched: boolean;
  onChange: (key: string, value: any) => void;
  onRegisterClear?: (clearFn: () => void) => void;
  allValues?: Record<string, any>;
}> = ({ field, value, error, touched, onChange, onRegisterClear, allValues }) => {
  const {
    options,
    loading,
    inputValue,
    selectedValue,
    handleInputChange,
    handleValueChange,
    handleFocus,
    clear: clearAutocomplete,
  } = useAutocomplete(
    field.fetchOptions as (query: string, context?: Record<string, any>) => Promise<SearchOption[]>,
    value,
    allValues,
    field.dependsOn
  ); // *** CHANGED: Use the new hook ***
  // Use the extracted hook to handle clear logic

  useAutocompleteClear(clearAutocomplete, onChange, field.key, onRegisterClear); // *** REMOVED DUPLICATE BLOCK ***
  // const clear = useCallback(...)
  // React.useEffect(...)

  return (
    <GlobalAutocomplete
      key={`${field.key}-${selectedValue ? 'has-value' : 'no-value'}`}
      fullWidth
      options={options}
      getOptionKey={getOptionKey}
      value={selectedValue}
      inputValue={inputValue}
      onChange={(_, newValue: any) => {
        if (typeof newValue === 'string') {
          onChange(field.key, newValue);
        } else {
          handleValueChange(newValue);
          onChange(field.key, newValue?.value || '');
        }
      }}
      onInputChange={(_, newInputValue, reason) => {
        handleInputChange(newInputValue); // If user is typing (not selecting an option), update the value
        if (reason === 'input' && field.freeSolo) {
          onChange(field.key, newInputValue);
        }
        if (reason === 'clear' && field.freeSolo) {
          onChange(field.key, '');
          handleValueChange(null);
        }
      }}
      onFocus={handleFocus}
      getOptionLabel={(option: any) => {
        if (typeof option === 'string') return option;
        return option.label || '';
      }}
      isOptionEqualToValue={(option: any, value: any) => {
        if (typeof option === 'string' || typeof value === 'string') {
          return option === value;
        }
        return option.value === value?.value;
      }}
      loading={loading}
      freeSolo={field.freeSolo}
      selectOnFocus
      clearOnBlur={false}
      handleHomeEndKeys
      slotProps={{
        listbox: {
          style: {
            maxWidth: '100%',
          },
        },
      }}
      renderInput={(params) => (
        <AutocompleteRenderInput params={params} field={field} error={error} touched={touched} />
      )}
    />
  );
};

// Main Autocomplete Field - delegates to static or dynamic implementation
const AutocompleteField: React.FC<{
  field: SearchFieldConfig;
  value: any;
  error: string;
  touched: boolean;
  onChange: (key: string, value: any) => void;
  onRegisterClear?: (clearFn: () => void) => void;
  allValues?: Record<string, any>;
}> = (props) => {
  // Check if field uses static options or dynamic fetchOptions
  const hasStaticOptions = props.field.options && props.field.options.length > 0;
  const hasFetchOptions = !!props.field.fetchOptions; // Use static autocomplete if field has pre-loaded options

  if (hasStaticOptions && !hasFetchOptions) {
    return <StaticAutocompleteField {...props} />;
  } // Use dynamic autocomplete if field has fetchOptions

  if (hasFetchOptions) {
    return <DynamicAutocompleteField {...props} />;
  } // Fallback: treat as static with empty options

  return <StaticAutocompleteField {...props} />;
};

// Helper function to calculate effective field with mobile override
const getEffectiveField = (field: SearchFieldConfig, isMobile: boolean): SearchFieldConfig => {
  if (isMobile && field.mobileOverride) {
    return {
      ...field,
      type: field.mobileOverride.type || field.type,
      label: field.mobileOverride.label || field.label,
      placeholder: field.mobileOverride.placeholder || field.placeholder,
    };
  }
  return field;
};

// Helper function to calculate date range constraints
const getDateRangeConstraints = (
  fieldKey: string,
  allValues?: Record<string, any>
): { minDate?: string; maxDate?: string } => {
  if (!allValues) return {};

  // Construction date validation (工期)
  if (fieldKey === 'construction_end_date' && allValues.construction_start_date) {
    return { minDate: allValues.construction_start_date };
  }
  if (fieldKey === 'construction_start_date' && allValues.construction_end_date) {
    return { maxDate: allValues.construction_end_date };
  }

  // Day date validation (エックス日) - for plans
  if (fieldKey === 'placementDateTo' && allValues.placementDateFrom) {
    return { minDate: allValues.placementDateFrom };
  }
  if (fieldKey === 'placementDateFrom' && allValues.placementDateTo) {
    return { maxDate: allValues.placementDateTo };
  }

  return {};
};

// Helper function to render clear button for text/number fields
const renderClearButton = (value: any, onClear: () => void) => {
  if (!value) return null;

  return (
    <InputAdornment position='end'>
      <IconButton size='small' onClick={onClear} edge='end' sx={{ padding: '4px' }}>
        <ClearIcon fontSize='small' />
      </IconButton>
    </InputAdornment>
  );
};

// Type for renderField parameters to avoid too many parameters
type RenderFieldParams = {
  field: SearchFieldConfig;
  value: any;
  error: string;
  touched: boolean;
  onChange: (key: string, value: any) => void;
  onRegisterClear?: (clearFn: () => void) => void;
  allValues?: Record<string, any>;
  isMobile?: boolean;
};

const renderField = ({
  field,
  value,
  error,
  touched,
  onChange,
  onRegisterClear,
  allValues,
  isMobile = false,
}: RenderFieldParams) => {
  const commonProps = {
    fullWidth: true,
    error: touched && !!error,
    helperText: touched && error ? error : '',
    size: 'small' as const,
  };

  // Apply mobile override if on mobile and override exists
  const effectiveField = getEffectiveField(field, isMobile);

  switch (effectiveField.type) {
    case 'text':
      return (
        <GlobalTextField
          {...commonProps}
          label={effectiveField.label}
          placeholder={effectiveField.placeholder}
          value={value || ''}
          onChange={(e) => onChange(field.key, e.target.value)}
          slotProps={{
            input: {
              endAdornment: renderClearButton(value, () => onChange(field.key, '')),
            },
          }}
        />
      );

    case 'number':
      return (
        <GlobalTextField
          {...commonProps}
          type='number'
          label={effectiveField.label}
          placeholder={effectiveField.placeholder}
          value={value || ''}
          onChange={(e) => onChange(field.key, e.target.value)}
          slotProps={{
            input: {
              endAdornment: renderClearButton(value, () => onChange(field.key, '')),
            },
          }}
        />
      );

    case 'date': {
      const { minDate, maxDate } = getDateRangeConstraints(field.key, allValues);

      return (
        <CustomDatePicker
          label={effectiveField.label}
          value={value || ''}
          onChange={(date) => onChange(field.key, date)}
          placeholder={effectiveField.placeholder}
          error={!!error}
          helperText={error}
          minDate={minDate}
          maxDate={maxDate}
        />
      );
    }

    case 'select':
      return (
        <GlobalFormControl {...commonProps} fullWidth>
          <InputLabel>{effectiveField.label}</InputLabel>
          <GlobalSelect
            value={value || ''}
            onChange={(e) => onChange(field.key, e.target.value)}
            label={effectiveField.label}
            MenuProps={{
              slotProps: {
                paper: {
                  sx: {
                    maxWidth: '300px',
                    '& .MuiMenuItem-root': {
                      whiteSpace: 'normal',
                      wordWrap: 'break-word',
                    },
                  },
                },
              },
            }}
          >
            {field.options?.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </GlobalSelect>
          {touched && error && <FormHelperText error>{error}</FormHelperText>}
        </GlobalFormControl>
      );

    case 'autocomplete':
      return (
        <AutocompleteField
          field={effectiveField}
          value={value}
          error={error}
          touched={touched}
          onChange={onChange}
          onRegisterClear={onRegisterClear}
          allValues={allValues}
        />
      );

    case 'checkbox':
      return (
        <FormControlLabel
          control={
            <Checkbox
              checked={value || false}
              onChange={(e) => onChange(field.key, e.target.checked)}
              color='primary'
            />
          }
          label={effectiveField.label}
          sx={{ margin: 0 }}
        />
      );

    case 'label':
      return (
        <Typography
          variant='body1'
          sx={{
            fontSize: '1.2rem',
            fontWeight: 500,
            color: '#666666',
            lineHeight: 1,
            userSelect: 'none',
          }}
        >
          {field.label}
        </Typography>
      );

    default:
      return null;
  }
};

// Helper: Check if current position is start of date range group
const isDateRangeStart = (fields: SearchFieldConfig[], index: number): boolean => {
  return (
    fields[index]?.type === 'date' &&
    fields[index + 1]?.type === 'label' &&
    fields[index + 2]?.type === 'date'
  );
};

// Helper: Check if field should be skipped (part of date range)
const shouldSkipField = (fields: SearchFieldConfig[], index: number): boolean => {
  // Skip if this is a label after a date
  if (index > 0 && fields[index - 1]?.type === 'date' && fields[index]?.type === 'label') {
    return true;
  } // Skip if this is an end date in a date range
  if (
    index > 1 &&
    fields[index - 2]?.type === 'date' &&
    fields[index - 1]?.type === 'label' &&
    fields[index]?.type === 'date'
  ) {
    return true;
  }
  return false;
};

// Helper: Render a single field (non-date-range) using renderOne
const renderOne = (
  field: SearchFieldConfig,
  state: any,
  updateField: (key: string, value: any) => void,
  handleRegisterAutocompleteClear: (key: string) => (clearFn: () => void) => void,
  isMobile: boolean = false
) => {
  return (
    <Box
      key={field.key}
      sx={{
        width: '100%',
        maxWidth: '100%',
        ...(field.type === 'label' && { maxWidth: '10px' }),
        ...(field.type === 'checkbox' && {
          gridColumn: { xs: '1 / -1', sm: '1 / -1', md: 'auto' },
          width: 'fit-content',
        }),
      }}
    >
      {renderField({
        field,
        value: state.values[field.key],
        error: state.errors[field.key],
        touched: state.touched[field.key],
        onChange: updateField,
        onRegisterClear: handleRegisterAutocompleteClear(field.key),
        allValues: state.values,
        isMobile,
      })}
    </Box>
  );
};

// Helper: Render date range group (start date + label + end date) using renderOne
const renderDateRangeGroup = (
  fields: SearchFieldConfig[],
  index: number,
  state: any,
  updateField: (key: string, value: any) => void,
  handleRegisterAutocompleteClear: (key: string) => (clearFn: () => void) => void,
  layout: 'compact' | 'default',
  isMobile: boolean = false
) => {
  const field = fields[index];
  const labelField = fields[index + 1];
  const endDateField = fields[index + 2];

  // On mobile, always use compact style regardless of layout
  const useCompactStyle = layout === 'compact' || isMobile;

  const commonBoxProps = useCompactStyle
    ? {
        gridColumn: { xs: '1', md: 'span 2' },
        display: 'flex',
        alignItems: 'flex-start',
        gap: 0.5,
        maxWidth: 'fit-content',
      }
    : {
        gridColumn: { xs: '1 / -1', sm: '1 / -1', md: 'span 2' },
        display: { xs: 'flex', sm: 'flex', md: 'grid' },
        gridTemplateColumns: { md: 'minmax(0,1fr) auto minmax(0,1fr)' },
        alignItems: 'flex-start',
        gap: { xs: 0.5, sm: 0.5, md: layout === 'default' ? 1 : 0.5 },
        width: '100%',
        minWidth: 0,
      };

  return (
    <Box key={field.key} sx={commonBoxProps}>
      <Box
        sx={{
          maxWidth: { xs: 'calc(50% - 15px)', sm: 'fit-content', md: '229px' },
          minWidth: { xs: '120px', sm: 'auto', md: 'auto' },
          height: '44px',
          flexShrink: 0,
        }}
      >
        {renderOne(field, state, updateField, handleRegisterAutocompleteClear, isMobile)}
      </Box>
      <Box
        sx={{
          maxWidth: '30px',
          flexShrink: 0,
          px: { xs: 0.5, sm: 0.5, md: 1 },
          pt: { xs: '8px', sm: '8px', md: useCompactStyle ? 0 : '8px' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {renderOne(labelField, state, updateField, handleRegisterAutocompleteClear, isMobile)}
      </Box>
      <Box
        sx={{
          maxWidth: { xs: 'calc(50% - 15px)', sm: 'fit-content', md: '229px' },
          minWidth: { xs: '120px', sm: 'auto', md: 'auto' },
          height: '44px',
          flexShrink: 0,
        }}
      >
        {renderOne(endDateField, state, updateField, handleRegisterAutocompleteClear, isMobile)}
      </Box>
    </Box>
  );
};

// Helper: Render a single field (non-date-range) using renderOne
const renderSingleField = (
  field: SearchFieldConfig,
  state: any,
  updateField: (key: string, value: any) => void,
  handleRegisterAutocompleteClear: (key: string) => (clearFn: () => void) => void,
  isMobile: boolean = false
) => {
  return renderOne(field, state, updateField, handleRegisterAutocompleteClear, isMobile);
};

// Helper: Render fields with date range detection
const renderFieldsWithDateRangeDetection = (
  fields: SearchFieldConfig[],
  state: any,
  updateField: (key: string, value: any) => void,
  handleRegisterAutocompleteClear: (key: string) => (clearFn: () => void) => void,
  layout: 'compact' | 'default' = 'default',
  isMobile: boolean = false
) => {
  return fields.map((field, index) => {
    if (shouldSkipField(fields, index)) {
      return null;
    }

    if (isDateRangeStart(fields, index)) {
      return renderDateRangeGroup(
        fields,
        index,
        state,
        updateField,
        handleRegisterAutocompleteClear,
        layout,
        isMobile
      );
    }

    return renderSingleField(field, state, updateField, handleRegisterAutocompleteClear, isMobile);
  });
};

// Helper: Render fields for custom grid layout with date range detection
const renderFieldsForCustomGrid = (
  fields: SearchFieldConfig[],
  state: any,
  updateField: (key: string, value: any) => void,
  handleRegisterAutocompleteClear: (key: string) => (clearFn: () => void) => void,
  isMobile: boolean = false
) => {
  return fields.map((field, index) => {
    // Skip if this field is part of a date range that should be grouped
    if (shouldSkipField(fields, index)) {
      return null;
    }

    // If this is the start of a date range, render the entire date range group in one cell
    if (isDateRangeStart(fields, index)) {
      const field1 = fields[index];
      const labelField = fields[index + 1];
      const field2 = fields[index + 2];

      // On mobile, use compact style with fit-content width
      return (
        <Box
          key={field1.key}
          sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 0.5,
          }}
        >
          <Box
            sx={{
              flex: isMobile ? undefined : 1,
              maxWidth: { xs: 'calc(50% - 15px)', sm: 'fit-content', md: 'none' },
              minWidth: { xs: '120px', sm: 'auto', md: 'auto' },
              flexShrink: 0,
            }}
          >
            {renderField({
              field: field1,
              value: state.values[field1.key],
              error: state.errors[field1.key],
              touched: state.touched[field1.key],
              onChange: updateField,
              onRegisterClear: handleRegisterAutocompleteClear(field1.key),
              allValues: state.values,
              isMobile,
            })}
          </Box>
          <Box
            sx={{
              minWidth: '30px',
              maxWidth: '30px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pt: '8px',
              px: { xs: 0.5, sm: 0.5, md: 0 },
              flexShrink: 0,
            }}
          >
            {renderField({
              field: labelField,
              value: state.values[labelField.key],
              error: state.errors[labelField.key],
              touched: state.touched[labelField.key],
              onChange: updateField,
              onRegisterClear: handleRegisterAutocompleteClear(labelField.key),
              allValues: state.values,
              isMobile,
            })}
          </Box>
          <Box
            sx={{
              flex: isMobile ? undefined : 1,
              maxWidth: { xs: 'calc(50% - 15px)', sm: 'fit-content', md: 'none' },
              minWidth: { xs: '120px', sm: 'auto', md: 'auto' },
              flexShrink: 0,
            }}
          >
            {renderField({
              field: field2,
              value: state.values[field2.key],
              error: state.errors[field2.key],
              touched: state.touched[field2.key],
              onChange: updateField,
              onRegisterClear: handleRegisterAutocompleteClear(field2.key),
              allValues: state.values,
              isMobile,
            })}
          </Box>
        </Box>
      );
    }

    // Regular single field
    return (
      <Box key={field.key} sx={{ width: '100%' }}>
        {renderField({
          field,
          value: state.values[field.key],
          error: state.errors[field.key],
          touched: state.touched[field.key],
          onChange: updateField,
          onRegisterClear: handleRegisterAutocompleteClear(field.key),
          allValues: state.values,
          isMobile,
        })}
      </Box>
    );
  });
};

export const GlobalSearch: React.FC<GlobalSearchProps> = ({
  config,
  className,
  variant = 'default',
  clearAutocomplete,
}) => {
  const isMobile = useIsMobile();

  // Filter fields based on showOnMobile (all fields show on web by default)
  const filteredFields = useMemo(() => {
    if (isMobile) {
      return config.fields.filter((field) => field.showOnMobile !== false); // Show if showOnMobile is not explicitly false
    }
    return config.fields; // On web, show all fields
  }, [config.fields, isMobile]);

  const { state, updateField, validateAll, clearAll, getSearchParams, hasValues } = useSearch(
    filteredFields,
    config.onChange,
    config.initialValues
  );
  // Default to collapsed (show only 2 fields) on mobile unless variant is 'expanded'
  // On web (desktop), always show all fields
  const [expanded, setExpanded] = useState(variant === 'expanded' || !isMobile);
  const [loading, setLoading] = useState(false);
  const autocompleteClearRefs = useRef<Map<string, () => void>>(new Map());

  // Check if should use custom grid layout (optional feature)
  const hasCustomLayout =
    config?.layout && config.layout !== 'default' && typeof config.layout === 'object';
  const customColumns =
    hasCustomLayout && typeof config.layout === 'object' ? config.layout.columns : 3;

  const handleSearch = async () => {
    if (!validateAll()) {
      return;
    }

    setLoading(true);
    try {
      // Get current search params
      const params = getSearchParams(); // Remove fields with empty string, null, or undefined
      const filteredParams = Object.fromEntries(
        Object.entries(params).filter(([_, v]) => v !== '' && v !== null && v !== undefined)
      );
      await config.onSearch(filteredParams);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    clearAll(); // Clear all autocomplete fields
    for (const clearFn of autocompleteClearRefs.current.values()) {
      clearFn();
    }
    if (config.onClear) {
      config.onClear();
    }
  };

  const handleRegisterAutocompleteClear = (fieldKey: string) => (clearFn: () => void) => {
    autocompleteClearRefs.current.set(fieldKey, clearFn); // Also register with parent component if provided
    if (clearAutocomplete) {
      clearAutocomplete(clearFn);
    }
  };

  // Auto-expand on desktop, collapse on mobile (unless variant is 'expanded')
  useEffect(() => {
    if (!isMobile) {
      // On desktop, always show all fields
      setExpanded(true);
    } else if (variant !== 'expanded') {
      // On mobile, start collapsed unless variant is 'expanded'
      setExpanded(false);
    }
  }, [isMobile, variant]);

  const toggleExpanded = () => {
    setExpanded(!expanded);
  };

  // Determine which fields to show based on variant, expanded state, and screen size
  // For mobile design: show only 2 fields initially, expand to show all when clicked
  // On web (desktop): always show all fields
  // If variant is 'expanded', show all fields without collapse functionality
  // Fields are already filtered by showOnMobile in filteredFields
  const shouldUseCompactMode = isMobile && variant !== 'expanded';

  // Use filteredFields directly (already filtered by showOnMobile)
  const fieldsToUse = filteredFields;

  // On mobile: separate fields that should always be visible from regular fields
  // Fields with alwaysVisible: true will be rendered separately after expand/collapse buttons
  // On web: keep alwaysVisible fields in the grid as normal
  const alwaysVisibleFields = isMobile
    ? fieldsToUse.filter((field) => field.alwaysVisible === true)
    : [];
  const regularFields = isMobile
    ? fieldsToUse.filter((field) => field.alwaysVisible !== true)
    : fieldsToUse;

  // When expanded on mobile, show all regular fields
  // When collapsed on mobile, show only first 2 regular fields
  // On web, always show all fields (including alwaysVisible fields)
  const visibleFields = shouldUseCompactMode ? regularFields.slice(0, 2) : regularFields;
  const allHiddenFields = shouldUseCompactMode ? regularFields.slice(2) : [];
  const hiddenFields = allHiddenFields;

  return (
    <SearchContainer className={className}>
      <SearchHeader>{/* <SearchTitle variant='h6'>検索条件</SearchTitle> */}</SearchHeader>

      <SearchForm>
        {(() => {
          if (hasCustomLayout) {
            // Custom Grid Layout (flexible columns) with date range detection
            return (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: `repeat(${Math.min(customColumns, 2)}, 1fr)`,
                    md: `repeat(${customColumns}, 1fr)`,
                  },
                  gap: 2,
                }}
              >
                {renderFieldsForCustomGrid(
                  visibleFields,
                  state,
                  updateField,
                  handleRegisterAutocompleteClear,
                  isMobile
                )}
              </Box>
            );
          }

          if (visibleFields.length === 5) {
            // Special layout for 5 fields to optimize space (existing behavior)
            return (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, 1fr)',
                    md: 'repeat(5, 1fr)',
                  },
                  gap: 2,
                }}
              >
                {renderFieldsWithDateRangeDetection(
                  visibleFields,
                  state,
                  updateField,
                  handleRegisterAutocompleteClear,
                  'compact',
                  isMobile
                )}
              </Box>
            );
          }

          // Default layout (existing behavior)
          return (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: visibleFields.length === 1 ? '1fr' : 'repeat(2, 1fr)',
                  md: getGridTemplateColumns(visibleFields.length),
                },
                gap: 2,
              }}
            >
              {renderFieldsWithDateRangeDetection(
                visibleFields,
                state,
                updateField,
                handleRegisterAutocompleteClear,
                'default',
                isMobile
              )}
            </Box>
          );
        })()}
        {shouldUseCompactMode && hiddenFields.length > 0 && !expanded && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              mt: 1,
            }}
          >
            <ExpandButton onClick={toggleExpanded} startIcon={<KeyboardArrowDownIcon />}>
              すべて表示
            </ExpandButton>
          </Box>
        )}
        <Collapse in={expanded}>
          <Box>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, 1fr)',
                  md: hasCustomLayout
                    ? `repeat(${customColumns}, 1fr)`
                    : getGridTemplateColumns(hiddenFields.length),
                },
                gap: 2,
                mt: 2,
              }}
            >
              {hasCustomLayout
                ? // Custom Grid for hidden fields with date range detection
                  renderFieldsForCustomGrid(
                    hiddenFields,
                    state,
                    updateField,
                    handleRegisterAutocompleteClear,
                    isMobile
                  )
                : // Default behavior for hidden fields
                  renderFieldsWithDateRangeDetection(
                    hiddenFields,
                    state,
                    updateField,
                    handleRegisterAutocompleteClear,
                    'compact',
                    isMobile
                  )}
            </Box>
            {shouldUseCompactMode && hiddenFields.length > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: '100%',
                  mt: 2,
                }}
              >
                <ExpandButton onClick={toggleExpanded} startIcon={<KeyboardArrowUpIcon />}>
                  閉じる
                </ExpandButton>
              </Box>
            )}
          </Box>
        </Collapse>
        {/* On mobile: Always show fields with alwaysVisible: true after expand/collapse buttons */}
        {/* On web: alwaysVisible fields are rendered in the grid above */}
        {alwaysVisibleFields.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              mt: 2,
            }}
          >
            {alwaysVisibleFields.map((field) => (
              <Box
                key={field.key}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                {renderField({
                  field,
                  value: state.values[field.key],
                  error: state.errors[field.key],
                  touched: state.touched[field.key],
                  onChange: updateField,
                  onRegisterClear: handleRegisterAutocompleteClear(field.key),
                  allValues: state.values,
                  isMobile,
                })}
              </Box>
            ))}
          </Box>
        )}
        <ActionButtons>
          <SearchButton
            variant='contained'
            startIcon={<SearchIcon />}
            onClick={handleSearch}
            disabled={loading}
          >
            {loading ? '検索中...' : '検索'}
          </SearchButton>
          <ClearButton
            variant='outlined'
            startIcon={<ClearIcon />}
            onClick={handleClear}
            disabled={!hasValues() || loading}
          >
            クリア
          </ClearButton>
        </ActionButtons>
      </SearchForm>
    </SearchContainer>
  );
};
